import { useEffect, useMemo, useState } from "react";
import Input from "#/components/shared/input";
import Textarea from "#/components/shared/textarea";
import Typography from "#/components/shared/typography";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import { ThemedSwitch } from "#/components/shared/themed-switch";

export type TemplateFormValues = {
  name: string;
  body: string;
  title: string;
  description: string;
  isActive: boolean;
  userVariableKeys: string;
};

export type TemplateFormPayload = {
  name: string;
  body: string;
  title?: string;
  description?: string;
  is_active?: boolean;
  user_variable_keys?: string[];
};

type TemplateFormErrors = {
  name?: string;
  title?: string;
  description?: string;
  userVariableKeys?: string;
  body?: string;
};

type TemplateFormProps = {
  initialValues: TemplateFormValues;
  onSubmit: (payload: TemplateFormPayload) => void;
  submitLabel: string;
  isSubmitting?: boolean;
  serverError?: string | null;
};

const emptyErrors: TemplateFormErrors = {};

export function TemplateForm({
  initialValues,
  onSubmit,
  submitLabel,
  isSubmitting = false,
  serverError,
}: TemplateFormProps) {
  const [values, setValues] = useState<TemplateFormValues>(initialValues);
  const [errors, setErrors] = useState<TemplateFormErrors>(emptyErrors);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const hasServerError = Boolean(serverError);

  const trimmedName = useMemo(() => values.name.trim(), [values.name]);

  const handleChange = (
    field: "name" | "body" | "title" | "description" | "userVariableKeys",
    value: string
  ) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: TemplateFormErrors = {};

    if (!trimmedName) {
      nextErrors.name = "Name is required.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const title = values.title.trim();
    const description = values.description.trim();
    const rawUserVariableKeys = values.userVariableKeys.trim();
    const userVariableKeys = rawUserVariableKeys
      ? rawUserVariableKeys
          .split(/[,\n]+/)
          .map((key) => key.trim())
          .filter(Boolean)
      : [];

    onSubmit({
      name: trimmedName,
      body: values.body,
      title: title || undefined,
      description: description || undefined,
      is_active: values.isActive,
      user_variable_keys: userVariableKeys.length > 0 ? userVariableKeys : undefined,
    });
  };

  return (
    <form
      className="flex flex-col gap-6 rounded-xl border border-beige-200 bg-white p-6 shadow-sm"
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col gap-1">
        <Typography variant="h3" className="text-brown-900">
          Template Details
        </Typography>
        <Typography variant="body2" className="text-brown-600">
          Draft templates can be edited before they are activated.
        </Typography>
      </div>

      {hasServerError ? (
        <div className="rounded-lg border border-terracotta/40 bg-cream-50 px-4 py-3">
          <Typography variant="body2" className="text-terracotta">
            {serverError}
          </Typography>
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <Input
          id="template-name"
          label="Name"
          placeholder="Template name"
          value={values.name}
          onChange={(event) => handleChange("name", event.target.value)}
          error={errors.name}
          fullWidth
          required
        />
        <Input
          id="template-status"
          label="Status"
          value="Draft"
          disabled
          fullWidth
        />
      </div>

      <Textarea
        id="template-body"
        label="Content"
        placeholder="Write the template content in Markdown or plain text."
        value={values.body}
        onChange={(event) => handleChange("body", event.target.value)}
        rows={10}
        fullWidth
      />

      <div className="flex flex-col gap-4">
        <Typography variant="h4" className="text-brown-900">
          Optional details
        </Typography>

        <Input
          id="template-title"
          label="Title"
          placeholder="Display title"
          value={values.title}
          onChange={(event) => handleChange("title", event.target.value)}
          fullWidth
        />

        <Textarea
          id="template-description"
          label="Description"
          placeholder="Short description for admins"
          value={values.description}
          onChange={(event) => handleChange("description", event.target.value)}
          rows={4}
          fullWidth
        />

        <Textarea
          id="template-user-variables"
          label="User variable keys"
          placeholder="first_name, appointment_date"
          value={values.userVariableKeys}
          onChange={(event) =>
            handleChange("userVariableKeys", event.target.value)
          }
          helperText="Comma or new-line separated."
          rows={3}
          fullWidth
        />

        <ThemedSwitch
          name="template-is-active"
          label="Active"
          description="Enable this template for use."
          value={values.isActive}
          onChange={(nextValue) =>
            setValues((current) => ({ ...current, isActive: nextValue }))
          }
          labelOnValue="On"
          labelOffValue="Off"
          size="sm"
          showSeparator={false}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <ActionButton type="submit" isLoading={isSubmitting}>
          {submitLabel}
        </ActionButton>
      </div>
    </form>
  );
}

export default TemplateForm;
